'use client'

import { Heart, LogOut, MapPin, Medal, PackageSearch, Settings } from "lucide-react"
import Skeleton from "@/components/elements/Skeleton"
import { formatImageURL, getTextColorStyles, getVendorPrimaryColor, isLoyaltyActive } from "@/utils/util"
import Avatar from "boring-avatars"
import Link from "next/link"
import { useProfileNav, type ProfileNavKey } from "@/lib/commerce/hooks"

const ICONS: Record<ProfileNavKey, any> = {
  orders: PackageSearch,
  loyalty: Medal,
  wishlist: Heart,
  addresses: MapPin,
  settings: Settings,
  logout: LogOut,
}

export default function ProfileCard({ customer, vendor, isLoading = false, setIsLoading, onLinkClick }: { customer: any, vendor: any, isLoading?: boolean, setIsLoading?: (isLoading: boolean) => void, onLinkClick?: () => void }) {
  const { accountItems, logoutItem } = useProfileNav()
  const visibleItems = accountItems.filter((item) => item.key !== 'loyalty' || isLoyaltyActive(vendor))
  const primaryColor = getVendorPrimaryColor(vendor)
  const { className: textColorClass, style: textColorStyle } = getTextColorStyles(primaryColor)

  return (
    <div className="py-4 px-3">
      {isLoading ? (
        <div className="flex flex-col justify-center items-center gap-y-6">
          <div className="w-16 h-16 rounded-full overflow-hidden">
            <Skeleton className="w-16 h-16" />
          </div>
          <div className="flex flex-col items-center gap-1">
            <Skeleton className="w-48 h-5" />
            <Skeleton className="w-32 h-4" />
          </div>
          <div className="flex flex-col gap-1 w-full">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="w-full h-10 rounded-lg" />
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="w-16 h-16 rounded-full overflow-hidden">
              {customer?.avatar ? (
                <img src={formatImageURL(customer.avatar)} alt="profile" className="w-full h-full object-cover" />
              ) : (
                <Avatar size={64} variant="marble" name={customer?.email || undefined} />
              )}
            </div>
            <div className="flex flex-col items-center text-center min-w-0 max-w-full">
              <h1 className="text-base font-semibold text-foreground truncate max-w-full">{customer?.firstName} {customer?.lastName}</h1>
              <p className="text-sm text-muted-foreground truncate max-w-full">{customer?.email}</p>
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase px-3 mb-1.5">Бүртгэл</div>
            <nav className="flex flex-col gap-1">
              {visibleItems.map((item) => {
                const Icon = ICONS[item.key]
                return (
                  <Link key={item.key} href={item.url} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${item.isActive ? `bg-muted ${textColorClass}` : 'text-foreground hover:bg-muted'}`} style={item.isActive ? textColorStyle : undefined} onClick={onLinkClick}>
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={2.5} />
                    <span className="flex-1">{item.name}</span>
                  </Link>
                )
              })}
            </nav>
          </div>

          {logoutItem && (
            <div className="pt-2 border-t border-border">
              <Link href={logoutItem.url} onClick={onLinkClick} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors">
                {(() => { const Icon = ICONS[logoutItem.key]; return <Icon className="h-4 w-4 shrink-0" strokeWidth={2.5} /> })()}
                <span className="flex-1">{logoutItem.name}</span>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}